// Cómo se dice y cómo se pinta el nivel del cliente. Fuente única: antes vivía en la pantalla
// Clientes del portal del vendedor y cada consumidor se armaba su propio mapa.
import { ClientLevelEnum } from '@fiado/type-kit/bin/loanScoring/enums/ClientLevelEnum';

/** El nivel tal como llega a la píldora: los del scoring, el empleado —que no puntúa— y la ausencia. */
export type TierValue = ClientLevelEnum | 'employee' | null;

/** Cuánto texto cabe: la ficha, la fila, o la celda angosta que solo admite tres letras. */
export type TierLabelMode = 'full' | 'short' | 'abbr';

/** El nivel de scoring en palabras. `null` es AUSENCIA de dato, no un nivel. */
export const TIER_LABEL: Record<ClientLevelEnum, string> = {
    [ClientLevelEnum.GOLD]: 'Oro',
    [ClientLevelEnum.SILVER]: 'Plata',
    [ClientLevelEnum.BRONZE]: 'Bronce',
    [ClientLevelEnum.NOT_ELIGIBLE]: 'Sin',
};

/** Abreviado para la celda «Niv», de 52px. `N/E` es el del mockup y no se confunde con el guion. */
export const TIER_SHORT: Record<ClientLevelEnum, string> = {
    [ClientLevelEnum.GOLD]: 'Oro',
    [ClientLevelEnum.SILVER]: 'Pla',
    [ClientLevelEnum.BRONZE]: 'Bro',
    [ClientLevelEnum.NOT_ELIGIBLE]: 'N/E',
};

const EMPLOYEE_LABEL = 'Empleado';
const EMPLOYEE_ABBR = 'Emp';
const NO_TIER_LABEL = 'Sin nivel';
const NO_TIER_ABBR = 'Sin';

/** El tier para una fila que ya dice «Nivel» en su etiqueta: «Oro», o «No elegible» si lo descartó. */
function tierRowLabel(level: ClientLevelEnum): string {
    return level === ClientLevelEnum.NOT_ELIGIBLE ? 'No elegible' : TIER_LABEL[level];
}

/** El chip completo de la ficha: «Nivel Oro», o «No elegible» cuando el scoring lo descartó. */
export function tierFullLabel(level: ClientLevelEnum | null): string {
    if (level === null) return NO_TIER_LABEL;
    const label = tierRowLabel(level);
    return level === ClientLevelEnum.NOT_ELIGIBLE ? label : `Nivel ${label}`;
}

/** El nivel en palabras, en la densidad que pide la pantalla. `null` se dice, no se calla. */
export function tierLabel(level: TierValue, mode: TierLabelMode): string {
    if (level === 'employee') return mode === 'abbr' ? EMPLOYEE_ABBR : EMPLOYEE_LABEL;
    if (level === null) return mode === 'abbr' ? NO_TIER_ABBR : NO_TIER_LABEL;
    if (mode === 'abbr') return TIER_SHORT[level];
    return mode === 'full' ? tierFullLabel(level) : tierRowLabel(level);
}

/** El gris neutro: la ausencia de nivel y el descarte del scoring no son un color de metal. */
const TIER_NONE_CLASS = 'border-tier-none-soft-border bg-tier-none-soft text-tier-none';

const TIER_TONE_CLASS: Record<ClientLevelEnum, string> = {
    [ClientLevelEnum.GOLD]: 'border-tier-gold-soft-border bg-tier-gold-soft text-tier-gold',
    [ClientLevelEnum.SILVER]: 'border-tier-silver-soft-border bg-tier-silver-soft text-tier-silver',
    [ClientLevelEnum.BRONZE]: 'border-tier-bronze-soft-border bg-tier-bronze-soft text-tier-bronze',
    [ClientLevelEnum.NOT_ELIGIBLE]: TIER_NONE_CLASS,
};

const EMPLOYEE_TONE_CLASS =
    'border-tier-employee-soft-border bg-tier-employee-soft text-tier-employee';

/** El relleno pastel del nivel. El scoring manda el valor en runtime: lo que no conozca cae al gris. */
export function tierToneClass(level: TierValue): string {
    if (level === 'employee') return EMPLOYEE_TONE_CLASS;
    if (level === null) return TIER_NONE_CLASS;
    return (TIER_TONE_CLASS as Partial<Record<ClientLevelEnum, string>>)[level] ?? TIER_NONE_CLASS;
}
